<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import { initializeApp } from 'firebase/app'
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  getRedirectResult,
  signInWithPopup,
} from 'firebase/auth'
import { firebaseConfig } from '@/composables/firebaseConfig'
import { api } from '@/api'
import { $getDeviceId, $getToken } from '@/helpers'
import { useToastStore } from '@/stores'
import { t } from '@/i18n/utils'

// ── Firebase ─────────────────────────────────────────────
initializeApp(firebaseConfig)
const auth = getAuth()

// ── Query params ─────────────────────────────────────────
const route = useRoute()
const from = computed(() => (route.query.from as string) || 'Profile')

// ── Login methods from AppConfig ─────────────────────────
const availableLogins = computed<string[]>(() => {
  try {
    const raw = localStorage.getItem('AppConfig')
    if (!raw)
      return []
    return JSON.parse(raw).login || []
  }
  catch { return [] }
})

const showFacebook = computed(() => availableLogins.value.includes('facebook'))
const showGoogle = computed(() => availableLogins.value.includes('google'))
const showApple = computed(() => availableLogins.value.includes('apple'))
const showAnyThird = computed(() => showFacebook.value || showGoogle.value || showApple.value)

// ── View state ───────────────────────────────────────────
type LoginView = 'main' | 'email'
const view = ref<LoginView>('main')
const loading = ref(false)

onMounted(async () => {
  // 如果没有任何三方登录可用，直接进入邮箱登录
  if (availableLogins.value.length === 0)
    view.value = 'email'
  else if (showAnyThird.value)
    view.value = 'main'
  else
    view.value = 'email'

  // 处理 signInWithPopup 降级为重定向后的回跳结果
  try {
    const result: any = await getRedirectResult(auth)
    if (result?.user?.providerData) {
      const type = sessionStorage.getItem('popupLoginType')
      if (type)
        await doLogin(type as ILoginType, result.user.providerData)
    }
  }
  catch (e: any) {
    console.error('PopupLogin redirect error', e.code, e.message)
  }
  finally {
    sessionStorage.removeItem('popupLoginType')
  }
})

// ── Communication ────────────────────────────────────────
function isInIframe() {
  try {
    return window.self !== window.top
  }
  catch {
    return true
  }
}

function postMessageToParent(msg: Record<string, unknown>) {
  if (isInIframe())
    window.parent.postMessage({ source: 'popup-login', ...msg }, '*')
}

// ── Login logic ──────────────────────────────────────────
type ILoginType = 'google.com' | 'facebook.com' | 'apple.com'

function getLoginTypeVal(type: ILoginType): string {
  if (type === 'facebook.com')
    return '2'
  if (type === 'google.com')
    return '1'
  return '3'
}

async function doLogin(type: ILoginType, providerData: any[]) {
  const params: Record<string, string> = {
    thirdType: getLoginTypeVal(type),
    thirdPartId: '',
    nickName: '',
    email: '',
    headImage: '',
  }
  providerData.forEach((item: any) => {
    if (item.providerId === type) {
      params.thirdPartId = item.uid
      params.nickName = item.displayName
      params.email = item.email
      params.headImage = item.photoURL
    }
  })
  if (type === 'apple.com' && params.email && !params.nickName)
    params.nickName = params.email

  if (!params.thirdPartId || !params.nickName) {
    postMessageToParent({ type: 'LOGIN_ERROR', message: 'Login failed, please try again.' })
    loading.value = false
    return
  }

  try {
    const res: any = await api.post('/member/bind/thirdPart', params, {
      headers: {
        equipmentId: $getDeviceId(),
        token: $getToken(),
      },
    })
    if (res.code === 0) {
      const toast = useToastStore()
      toast.$toast(t('LoginSuccessful'))
      postMessageToParent({ type: 'LOGIN_SUCCESS', token: res.data.token, message: t('LoginSuccessful') })
    }
    else { postMessageToParent({ type: 'LOGIN_ERROR', message: res.msg || 'Login failed' }) }
  }
  catch (e: any) {
    postMessageToParent({ type: 'LOGIN_ERROR', message: e?.msg || 'Login failed, please try again.' })
  }
  finally {
    loading.value = false
  }
}

async function signInWithGoogle() {
  loading.value = true
  sessionStorage.setItem('popupLoginType', 'google.com')
  try {
    const provider = new GoogleAuthProvider()
    const result = await signInWithPopup(auth, provider)
    sessionStorage.removeItem('popupLoginType')
    await doLogin('google.com', result.user.providerData)
  }
  catch (e: any) {
    if (e?.code !== 'auth/popup-closed-by-user' && e?.code !== 'auth/cancelled-popup-request')
      postMessageToParent({ type: 'LOGIN_ERROR', message: 'Login failed, please try again.' })
    sessionStorage.removeItem('popupLoginType')
    loading.value = false
  }
}

async function signInWithFacebook() {
  loading.value = true
  sessionStorage.setItem('popupLoginType', 'facebook.com')
  try {
    const provider = new FacebookAuthProvider()
    provider.addScope('email,public_profile')
    const result = await signInWithPopup(auth, provider)
    sessionStorage.removeItem('popupLoginType')
    await doLogin('facebook.com', result.user.providerData)
  }
  catch (e: any) {
    if (e?.code !== 'auth/popup-closed-by-user' && e?.code !== 'auth/cancelled-popup-request')
      postMessageToParent({ type: 'LOGIN_ERROR', message: 'Login failed, please try again.' })
    sessionStorage.removeItem('popupLoginType')
    loading.value = false
  }
}

async function signInWithApple() {
  loading.value = true
  sessionStorage.setItem('popupLoginType', 'apple.com')
  try {
    const provider = new OAuthProvider('apple.com')
    provider.addScope('email')
    provider.addScope('name')
    const result = await signInWithPopup(auth, provider)
    sessionStorage.removeItem('popupLoginType')
    await doLogin('apple.com', result.user.providerData)
  }
  catch (e: any) {
    if (e?.code !== 'auth/popup-closed-by-user' && e?.code !== 'auth/cancelled-popup-request')
      postMessageToParent({ type: 'LOGIN_ERROR', message: 'Login failed, please try again.' })
    sessionStorage.removeItem('popupLoginType')
    loading.value = false
  }
}

// ── Email login ──────────────────────────────────────────
const emailForm = ref({
  email: '',
  code: '',
  timeout: 0,
  sending: false,
  verifying: false,
})

let countdownTimer: ReturnType<typeof setInterval> | undefined

async function getEmailCode() {
  if (emailForm.value.timeout > 0 || emailForm.value.sending)
    return
  if (!emailForm.value.email) {
    postMessageToParent({ type: 'LOGIN_ERROR', message: 'Please enter your email.' })
    return
  }
  emailForm.value.sending = true
  try {
    await api.post('/member/email', { toAddress: emailForm.value.email }, {
      headers: { equipmentId: $getDeviceId(), token: $getToken() },
    })
    emailForm.value.timeout = 60
    countdownTimer = setInterval(() => {
      emailForm.value.timeout--
      if (emailForm.value.timeout <= 0)
        clearInterval(countdownTimer)
    }, 1000)
  }
  catch (e: any) {
    postMessageToParent({ type: 'LOGIN_ERROR', message: e?.msg || 'Failed to send code.' })
  }
  finally {
    emailForm.value.sending = false
  }
}

async function signInWithEmail() {
  if (emailForm.value.verifying)
    return
  if (!emailForm.value.code) {
    postMessageToParent({ type: 'LOGIN_ERROR', message: 'Please enter verification code.' })
    return
  }
  emailForm.value.verifying = true
  try {
    const res: any = await api.post('/member/bind/thirdPart', {
      verifyCode: emailForm.value.code,
      thirdPartId: emailForm.value.email,
      thirdType: 7,
    }, {
      headers: { equipmentId: $getDeviceId(), token: $getToken() },
    })
    if (res.code === 0) {
      const toast = useToastStore()
      toast.$toast(t('LoginSuccessful'))
      postMessageToParent({ type: 'LOGIN_SUCCESS', token: res.data.token, message: t('LoginSuccessful') })
    }
    else { postMessageToParent({ type: 'LOGIN_ERROR', message: res.msg || 'Login failed' }) }
  }
  catch (e: any) {
    postMessageToParent({ type: 'LOGIN_ERROR', message: e?.msg || 'Login failed, please try again.' })
  }
  finally {
    emailForm.value.verifying = false
  }
}

function goBack() {
  view.value = 'main'
}

// ── Resources ────────────────────────────────────────────
const brandLogo = computed(() => {
  // 使用对应品牌的图片资源
  const brand = import.meta.env.VITE_BRAND || 'rocnovel'
  return {
    logo: `/images/${brand}/login/roc-logo.png`,
    novelText: `/images/${brand}/login/roc-novel.png`,
    emailIcon: `/images/${brand}/login/icon-email.png`,
    emailCodeIcon: `/images/${brand}/login/icon-email-code.png`,
    facebookIcon: `/images/${brand}/login/icon-facebook-logo.png`,
    googleIcon: `/images/${brand}/login/icon-google-logo.png`,
    appleIcon: `/images/${brand}/login/icon-apple-logo.png`,
  }
})

const userAgreementUrl = import.meta.env.VITE_USER_AGREEMENT_URL
const privacyPolicyUrl = import.meta.env.VITE_PRIVACY_POLICY_URL
</script>

<template>
  <div class="popup-login-page">
    <!-- 三方登录视图 -->
    <template v-if="view === 'main'">
      <div class="main-body">
        <div class="brand-logos">
          <img :src="brandLogo.logo" alt="logo" class="brand-icon">
          <img :src="brandLogo.novelText" alt="logo text" class="brand-text">
        </div>

        <div v-if="showAnyThird" class="btn-group">
          <button
            v-if="showFacebook"
            class="third-btn facebook-btn"
            :disabled="loading"
            @click="signInWithFacebook"
          >
            <img :src="brandLogo.facebookIcon" alt="Facebook" class="btn-icon">
            <span>Sign in with Facebook</span>
          </button>
          <button
            v-if="showGoogle"
            class="third-btn google-btn"
            :disabled="loading"
            @click="signInWithGoogle"
          >
            <img :src="brandLogo.googleIcon" alt="Google" class="btn-icon">
            <span>Sign in with Google</span>
          </button>
          <button
            v-if="showApple"
            class="third-btn apple-btn"
            :disabled="loading"
            @click="signInWithApple"
          >
            <img :src="brandLogo.appleIcon" alt="Apple" class="btn-icon">
            <span>Sign in with Apple</span>
          </button>
        </div>

        <button v-if="showAnyThird" class="email-link" @click="view = 'email'">
          Continue with Email
        </button>

        <template v-if="!showAnyThird && availableLogins.length === 0">
          <p style="color: rgba(101,104,111,1); font-size: 14px; margin-top: 24px;">
            Loading login options...
          </p>
        </template>

        <div class="terms-row">
          <span class="terms-line" />
          <p class="terms-text">
            If you continue, you agree to the
            <a :href="userAgreementUrl" target="_blank">User Agreement</a>
            and
            <a :href="privacyPolicyUrl" target="_blank">Privacy Policy</a>
          </p>
          <span class="terms-line" />
        </div>
      </div>
    </template>

    <!-- 邮箱登录视图 -->
    <template v-else>
      <div class="email-header">
        <button class="back-btn" @click="goBack">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M12 4L6 10L12 16"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <h3 class="email-title">
          Sign in with Email
        </h3>
      </div>
      <div class="popup-divider" />

      <div class="email-body">
        <p class="field-label">
          Your Email
        </p>
        <div class="input-wrap">
          <img :src="brandLogo.emailIcon" alt="" class="input-icon">
          <input
            v-model="emailForm.email"
            type="email"
            class="text-input"
            placeholder=""
            maxlength="50"
          >
        </div>

        <p class="field-label">
          Verification code
        </p>
        <div class="input-wrap">
          <img :src="brandLogo.emailCodeIcon" alt="" class="input-icon">
          <input
            v-model="emailForm.code"
            type="text"
            class="text-input"
            placeholder=""
            maxlength="6"
          >
          <button
            v-if="emailForm.timeout > 0"
            class="code-countdown"
            disabled
          >
            {{ emailForm.timeout }}s
          </button>
          <button
            v-else
            class="code-get"
            :disabled="emailForm.sending"
            @click="getEmailCode"
          >
            Get
          </button>
        </div>

        <button
          class="signin-btn"
          :disabled="emailForm.verifying"
          @click="signInWithEmail"
        >
          Sign in
        </button>

        <div class="terms-row">
          <span class="terms-line" />
          <p class="terms-text">
            If you continue, you agree to the
            <a :href="userAgreementUrl" target="_blank">User Agreement</a>
            and
            <a :href="privacyPolicyUrl" target="_blank">Privacy Policy</a>
          </p>
          <span class="terms-line" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.popup-login-page {
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

/* ── 三方登录主体 ── */
.main-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 48px 32px;
}

.brand-logos {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.brand-icon {
  width: 40px;
  height: 40px;
  object-fit: contain;
}
.brand-text {
  width: 148px;
  object-fit: contain;
}

.btn-group {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 32px;
  width: 100%;
  align-items: center;
}

.third-btn {
  position: relative;
  width: 300px;
  height: 45px;
  border-radius: 25px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
  cursor: pointer;
  padding-left: 56px;
  padding-right: 16px;
  box-sizing: border-box;
  transition: opacity 0.2s;
}
.third-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.third-btn span {
  display: block;
  width: 100%;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn-icon {
  position: absolute;
  left: 29px;
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.facebook-btn {
  background: rgba(26, 119, 242, 1);
}
.google-btn {
  background: rgba(35, 159, 251, 1);
}
.apple-btn {
  background: rgba(40, 40, 40, 1);
}

.email-link {
  margin-top: 24px;
  background: none;
  border: none;
  font-size: 16px;
  font-weight: 500;
  color: rgba(152, 117, 251, 1);
  cursor: pointer;
  text-align: center;
}
.email-link:hover {
  text-decoration: underline;
}

/* ── 协议 ── */
.terms-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 28px;
  width: 100%;
}
.terms-line {
  flex: 1;
  height: 1px;
  background: rgba(168, 172, 183, 1);
}
.terms-text {
  font-size: 12px;
  color: rgba(146, 150, 160, 1);
  text-align: center;
  line-height: 1.6;
  margin: 0;
}
.terms-text a {
  color: rgba(146, 150, 160, 1);
  text-decoration: underline;
}

/* ── 邮箱登录头部 ── */
.email-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 20px 16px;
  flex-shrink: 0;
}
.back-btn {
  position: absolute;
  left: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: rgba(50, 53, 58, 1);
  display: flex;
  align-items: center;
}
.email-title {
  font-size: 18px;
  font-weight: 700;
  color: rgba(50, 52, 53, 1);
  margin: 0;
}
.popup-divider {
  height: 1px;
  background: rgba(243, 245, 250, 1);
  flex-shrink: 0;
}

/* ── 邮箱登录表单 ── */
.email-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 48px 32px;
}
.field-label {
  width: 300px;
  font-size: 14px;
  color: rgba(101, 104, 111, 1);
  margin: 0 0 8px;
}
.field-label + .field-label,
.input-wrap + .field-label {
  margin-top: 16px;
}

.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 300px;
  height: 45px;
  border-radius: 23px;
  border: 1px solid rgba(232, 229, 242, 1);
  background: #ffffff;
  box-sizing: border-box;
}
.input-icon {
  position: absolute;
  left: 16px;
  width: 16px;
  height: 16px;
  object-fit: contain;
}
.text-input {
  flex: 1;
  height: 100%;
  padding: 0 12px 0 42px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: rgba(45, 57, 69, 1);
  border-radius: 23px;
  min-width: 0;
}

.code-countdown,
.code-get {
  flex-shrink: 0;
  padding: 0 16px 0 0;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
}
.code-countdown {
  color: rgba(146, 150, 160, 1);
  cursor: default;
}
.code-get {
  color: rgba(152, 117, 251, 1);
}
.code-get:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.signin-btn {
  width: 300px;
  height: 45px;
  border-radius: 23px;
  background: rgba(152, 117, 251, 1);
  border: none;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.88px;
  color: #ffffff;
  cursor: pointer;
  margin-top: 28px;
  transition: opacity 0.2s;
}
.signin-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.signin-btn:hover:not(:disabled) {
  opacity: 0.9;
}
</style>
